import { formatInTimeZone, getTimeZoneLabel, parseUTCToLocal } from './dateUtils';

/**
 * Read-side helpers for a test cycle as the backend projects it (plan §A.2):
 * the cycle document, joined to its structure name and its version.
 */

/**
 * When the confirmation window opens — the cycle's start minus its window.
 * The backend derives the enrolment cutoff but not this, so it is computed here.
 * @param {Object} cycle - Projected cycle
 * @returns {Date|null} - Window open moment, null for a manually started cycle
 */
export const getWindowOpensAt = (cycle) => {
    const startAt = parseUTCToLocal(cycle?.start_at);
    if (!startAt) return null;

    const minutes = cycle?.confirmation_window_minutes || 0;
    return new Date(startAt.getTime() - minutes * 60 * 1000);
};

/**
 * The one line a cohort gives its calendar, which is not always its start: a cycle
 * already running is read for when it ends, one that has ended for when it ended,
 * and a manual-start one has no start to name at all — the team picks the day and
 * emails everyone enrolled. Every moment is on the cohort's own clock, named.
 *
 * Shared by the catalog card and the home page's cycle row, which used to read
 * `start_at` alone and so said nothing at all about a cohort the team had started.
 * @param {Object} cycle - Projected cycle
 * @returns {string} - A sentence fragment: "Ends Sep 26, 2026, 02:54 p.m. EDT"
 */
export const cycleScheduleLine = (cycle) => {
    if (!cycle) return '';
    const timeZone = cycle.display_timezone;
    const at = (value) => `${formatInTimeZone(value, timeZone)} ${getTimeZoneLabel(value, timeZone)}`.trim();

    if (cycle.status === 'ended') return cycle.ended_at ? `Ended ${at(cycle.ended_at)}` : 'Ended';
    if (cycle.status === 'cancelled') return 'Cancelled';
    if (cycle.status === 'running') return cycle.ends_at ? `Ends ${at(cycle.ends_at)}` : 'Running now';
    if (cycle.start_at) return `Starts ${at(cycle.start_at)}`;
    if (cycle.status === 'confirmation') return 'Starts when the team closes the window';
    return 'Start date not set yet';
};

const MINUTES_PER_DAY = 24 * 60;

const countOf = (value, unit) => `${value} ${unit}${value === 1 ? '' : 's'}`;

/**
 * A span as a candidate reads it. A cohort is authored in minutes but lived in
 * days, so anything a day or longer is quoted in days — 7200 minutes is "5 days",
 * never "120h" — and hours and minutes are kept for the spans that are really
 * shorter than a day.
 * @param {number} minutes - A span in minutes
 * @returns {string} - e.g. "5 days", "2 days 6 hours", "3 hours 30 minutes", "45 minutes"
 */
export const formatMinutes = (minutes) => {
    if (!minutes || minutes < 1) return '—';

    const whole = Math.floor(minutes);
    const parts = [
        [Math.floor(whole / MINUTES_PER_DAY), 'day'],
        [Math.floor((whole % MINUTES_PER_DAY) / 60), 'hour'],
        [whole % 60, 'minute'],
    ].filter(([value]) => value > 0);

    return parts.map(([value, unit]) => countOf(value, unit)).join(' ');
};

/**
 * @param {Object} version - The cycle's structure version
 * @returns {string} - How candidates are arranged during the trial
 */
export const formatTrialFormat = (version) => {
    if (!version?.has_groups) return 'On your own';
    return version.group_size ? `In a team of ${version.group_size}` : 'In a team';
};

/**
 * The cohort's clock said as a place rather than as a database id: a candidate is
 * told "Toronto time", never "America/Toronto".
 * @param {string} timeZone - IANA timezone name
 * @returns {string} - e.g. "Toronto time", empty when there is no zone
 */
export const formatZoneName = (timeZone) => {
    const city = String(timeZone || '').split('/').pop().replace(/_/g, ' ').trim();
    if (!city) return '';
    return /^(UTC|GMT)$/i.test(city) ? city.toUpperCase() : `${city} time`;
};

/**
 * The same, with the abbreviation the clock is actually reading in: "Toronto time
 * (EDT)".
 *
 * Finding B6 — the workspace showed a student `America/Toronto`, in monospace, as
 * their working-day setting. The id stays the stored value everywhere; this is only
 * what is printed. The abbreviation is asked of `Intl` rather than tabulated, so it
 * is right on both sides of a daylight change, and a browser that cannot answer
 * leaves the place-name standing on its own.
 *
 * @param {string} timeZone - IANA timezone name
 * @param {Date} [at] - The moment the abbreviation is read at
 * @returns {string}
 */
export const formatZoneLabel = (timeZone, at = new Date()) => {
    const place = formatZoneName(timeZone);
    if (!place) return '';

    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone, timeZoneName: 'short',
        }).formatToParts(at);
        const abbreviation = parts.find((part) => part.type === 'timeZoneName')?.value || '';
        /* "GMT-4" is the offset wearing a name badge, and "UTC (UTC)" is the place
           saying its own name twice; neither tells a student anything the place has
           not already told them. */
        const useful = abbreviation
            && !/^(GMT|UTC)[+-]/.test(abbreviation)
            && abbreviation.toLowerCase() !== place.toLowerCase();
        return useful ? `${place} (${abbreviation})` : place;
    } catch (error) {
        return place;
    }
};

/**
 * @param {Object} cycle - Projected cycle
 * @returns {string} - Remaining spots as a candidate should read them
 */
export const formatSpots = (cycle) => {
    const remaining = cycle?.spots_remaining;
    if (remaining === null || remaining === undefined) return 'Unlimited';
    if (remaining < 1) return 'Full';
    return `${remaining} left`;
};

/**
 * @param {Object} cycle - Projected cycle
 * @returns {string} - Remaining spots as a sentence fragment for a card
 */
export const formatSpotsPhrase = (cycle) => {
    const remaining = cycle?.spots_remaining;
    if (remaining === null || remaining === undefined) return 'Unlimited spots';
    if (remaining < 1) return 'No spots left';
    return `${remaining} spot${remaining === 1 ? '' : 's'} left`;
};

export const isCycleFull = (cycle) =>
    cycle?.spots_remaining !== null && cycle?.spots_remaining !== undefined && cycle.spots_remaining < 1;

/**
 * Whether enrolment is still being taken. This mirrors the backend check that
 * actually decides it (status open, before the cutoff) — the UI only ever
 * disables ahead of it, never enables something the server would refuse.
 * @param {Object} cycle - Projected cycle
 * @param {number} now - Current epoch milliseconds
 * @returns {boolean} - True while enrolment is open
 */
export const isEnrolmentOpen = (cycle, now) => {
    if (cycle?.status !== 'open') return false;

    const cutoff = parseUTCToLocal(cycle.enrolment_cutoff_at);
    if (!cutoff) return true;

    return now < cutoff.getTime();
};

/**
 * The version a candidate sees on a cycle. The cycle carries a denormalized
 * copy; the joined version document is the fallback.
 * @param {Object} cycle - Projected cycle
 * @returns {string} - e.g. "1.2", empty when unknown
 */
/* Nothing in the product renders this. A candidate is not shown which revision of a
   structure their cohort was cut from — the owner ruled versioning is not surfaced to
   them anywhere in a trial — so the reader is kept for the shape it parses and for the
   day something staff-facing needs it, and has no caller in this app. */
export const getVersionLabel = (cycle) => {
    const label = cycle?.version_number || cycle?.version?.version_number || '';
    return label === 0 ? '' : String(label);
};

/**
 * The participation a certificate names outright, or null when it names none.
 *
 * `work_trial_id` before `cycle_id` because a trial is one participation and a cohort is a
 * roomful; either is an answer rather than a guess. A certificate carrying an id that matches
 * no row here belongs to a participation this list does not hold, so it is not then handed to
 * the structure-and-version guess — an id that says "not this one" is still an answer.
 */
const identifiedRow = (certificate, holders) => {
    const trialId = certificate?.work_trial_id;
    const cycleId = certificate?.cycle_id;
    if (!trialId && !cycleId) return null;
    return holders.find(({ cycle, enrolment }) => (
        (trialId && trialId === enrolment.work_trial_id) || (cycleId && cycleId === cycle.cycle_id)
    )) || null;
};

/**
 * Joins each active certificate to the participation it was issued for.
 *
 * On the id when the payload carries one. `GET /me/certificates` does not today:
 * `project_own_certificate` serves the public facts plus `share_enabled`, and the public
 * projection deliberately withholds every pointer back to a trial. Until it adds
 * `work_trial_id` (the Certificate record already holds it, beside `cycle_id`), a certificate
 * names only its structure and version, so a retake of one version falls to the guess below:
 * the cohort that ended last before the issue, or else the one that ended first.
 *
 * @param {Array} rows - `{ enrolment, cycle }` rows, as `GET /me/enrolments` serves them
 * @param {Array} certificates - The candidate's certificates
 * @returns {Object} - `{ [cycle_id]: certificate }`, the latest issued when several fall to one row
 */
export const certificatesByCycle = (rows, certificates) => {
    const momentOf = (value) => parseUTCToLocal(value)?.getTime() ?? null;
    // A no-show never had a trial, so it cannot hold a certificate.
    const holders = (rows || []).filter(row => row?.cycle?.cycle_id && row.enrolment?.work_trial_id);
    const byCycle = {};

    (certificates || [])
        .filter(certificate => certificate?.status === 'active')
        .forEach((certificate) => {
            const issued = momentOf(certificate.issued_at);
            let row = identifiedRow(certificate, holders);

            if (!row) {
                if (certificate.work_trial_id || certificate.cycle_id) return;
                const matches = holders
                    .filter(({ cycle }) => certificate.structure_name === cycle.structure_name
                        && String(certificate.structure_version ?? '') === String(cycle.version_number ?? ''))
                    .map(candidate => ({
                        row: candidate,
                        end: momentOf(candidate.cycle.ended_at || candidate.cycle.ends_at || candidate.cycle.cancelled_at),
                    }));
                if (matches.length === 0) return;

                const endedBefore = matches.filter(({ end }) => issued !== null && end !== null && end <= issued);
                ({ row } = endedBefore.length > 0
                    ? endedBefore.reduce((latest, match) => (match.end > latest.end ? match : latest))
                    : matches.reduce((earliest, match) => (
                        (match.end ?? Infinity) < (earliest.end ?? Infinity) ? match : earliest
                    )));
            }

            const held = byCycle[row.cycle.cycle_id];
            if (!held || (issued ?? -Infinity) > (momentOf(held.issued_at) ?? -Infinity)) {
                byCycle[row.cycle.cycle_id] = certificate;
            }
        });

    return byCycle;
};
